<script setup lang="ts">
import { computed } from 'vue';
import ClubGrid from '@/components/club/ClubGrid.vue';
import ClubStatsSummary from '@/components/club/ClubStatsSummary.vue';
import { useDebugUser } from '@/composables/useDebugUser';
import Container from '@/layouts/Container.vue';
import { useClubData } from '@/services/clubDataService';

// Page definition for routing
definePage({
  name: 'clubdata',
});

// Get current user
const user = useDebugUser();
const userId = computed(() => user.value?.uid);

// Get club data using our composable
const { clubData, error } = useClubData();
</script>

<template>
  <Container>
    <template #title>
      Club Performance Analysis
    </template>

    <p class="text-gray-700 dark:text-gray-300 mb-6">
      This page shows your club performance statistics based on your recorded shots.
      Data includes average distance (based on your best 10 shots), dispersion, and shot count for each club.
    </p>

    <div v-if="error" class="bg-red-50 border border-red-300 rounded-lg p-4 mb-6 text-red-800">
      <p>{{ error.message }}</p>
    </div>

    <div v-else-if="!userId" class="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 mb-6">
      <i class="pi pi-user-minus text-3xl text-gray-400 mb-2" />
      <p class="text-gray-600 text-lg mb-2">
        Please login to view your club data
      </p>
      <router-link to="/login" class="inline-block text-blue-600 hover:underline">
        Go to Login
      </router-link>
    </div>

    <div v-else-if="(!clubData || clubData.length === 0)" class="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 mb-6">
      <i class="pi pi-info-circle text-3xl text-gray-400 mb-2" />
      <p class="text-gray-600 text-lg mb-2">
        No club data available
      </p>
      <p class="text-gray-500 mb-4">
        Upload your shot data to see club performance statistics
      </p>
      <router-link to="/upload" class="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
        Upload Data
      </router-link>
    </div>

    <ClubStatsSummary :clubs="clubData || []" />
    <ClubGrid :clubs="clubData || []" />
  </Container>
</template>

<style scoped>
:deep(.p-tabview-nav) {
  border-bottom-color: #e5e7eb;
}

:deep(.p-tabview-selected) {
  color: #1e40af !important;
  border-bottom-color: #1e40af !important;
}

:deep(.p-tabview-nav-link) {
  color: #4b5563;
}
</style>
